import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import {
  ListItem,
  Avatar,
  Link,
  useTheme,
  Box,
  Typography,
  useMediaQuery,
  Checkbox,
} from '@mui/material';
import { Lock, Users, Upload } from 'lucide-mui';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FileType, FolderType } from '../../../../types';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import FileActionMenu from '../FileActionMenu';
import { RootState } from '../../../../reducers';
import defaultThumbnail from '../../../../assets/recording/DefaultThumbnail.svg';

interface FileListItemProps {
  id?: string;
  file: FileType;
  isSelected: boolean;
  selectMode?: boolean;
  onSelect?: (file: FileType) => void;
  fileUrl: string;
  freeUserTimeLimit: Date | null;
}

const formatDuration = (seconds: number | undefined): string => {
  if (typeof seconds !== 'number') return '0:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return hours > 0
    ? `${hours}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    : `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Copied from PendingImport for import animations
const ProcessingListItem = ({ name }: { name: string }) => {
  const [message, setMessage] = useState('Processing...');

  useEffect(() => {
    // Start the message cycling sequence
    const messageSequence = async () => {
      setTimeout(() => setMessage('Importing your file..'), 2000);

      setTimeout(() => {
        const cyclingMessages = [
          'This may take a few moments..',
          'Processing your file..',
          'Importing..',
          'Still Working..',
        ];

        let messageIndex = 0;
        const interval = setInterval(() => {
          setMessage(cyclingMessages[messageIndex]);
          messageIndex = (messageIndex + 1) % cyclingMessages.length;
        }, 5000);

        return () => clearInterval(interval);
      }, 5000);
    };

    messageSequence();
  }, []);

  return (
    <ListItem
      disablePadding
      sx={{
        borderBottom: 1,
        borderColor: 'secondary.light',
        position: 'relative',
        py: 1.5,
        pr: 2,
        opacity: 0.8,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(200px, 1fr) 80px',
            sm: 'minmax(200px, 1.5fr) 120px',
            md: 'minmax(200px, 1.5fr) 200px',
            lg: 'minmax(250px, 1.5fr) 400px',
          },
          alignItems: 'center',
          width: '100%',
          gap: { xs: 1, sm: 2 },
          pl: 2,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: { xs: 24, sm: 32 },
              height: { xs: 24, sm: 32 },
              backgroundColor: 'secondary.main',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Upload sx={{ fontSize: '1.2rem' }} />
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Upload
            sx={{
              fontSize: '1.5rem',
              animation: 'float 2s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-5px)' },
              },
              opacity: 0.9,
              color: 'secondary.main',
            }}
          />
        </Box>
      </Box>
    </ListItem>
  );
};

const FileListItem = ({
  file,
  fileUrl,
  freeUserTimeLimit,
  isSelected,
  selectMode,
  onSelect,
}: FileListItemProps) => {
  const { name, thumbUrl, createdAt, owner, duration, parentId, type } = file;
  const { name: ownerName } = owner;
  const [thumbUrlValid, setThumbUrlValid] = useState<string>(
    thumbUrl || defaultThumbnail
  );
  const [showMenu, setShowMenu] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
  const touchStartTime = useRef<number>(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { folderId } = useParams<{ folderId: string }>();
  const isInRecentsView = folderId === 'recents';
  const prevTypeRef = useRef<string | undefined>(type);

  // Check if the file type is Import
  const isImportedFile = type === 'Import';

  // Effect to update thumbnail when file type changes from Import to regular
  useEffect(() => {
    // If the file was previously an Import and now it's not, update the thumbnail
    if (prevTypeRef.current === 'Import' && type !== 'Import') {
      if (thumbUrl) {
        setThumbUrlValid(thumbUrl);
      }
    }

    // Always update the ref to the current type for next comparison
    prevTypeRef.current = type;

    // Explicit return
    return undefined;
  }, [type, thumbUrl]);

  const handleTouchStart = () => {
    touchStartTime.current = Date.now();
    longPressTimer.current = setTimeout(() => {
      setShowMenu(true);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const parentFolder = useSelector((state: RootState) => {
    return state.library.folders.data.find(
      (folder: FolderType) => folder._id === parentId
    );
  });

  let hiddenForFreeUser = false;

  if (freeUserTimeLimit) {
    hiddenForFreeUser =
      new Date(createdAt).getTime() < new Date(freeUserTimeLimit).getTime();
  }
  const handleMoveClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
    },
    [dispatch, file]
  );

  const handleSelectionClick = (e: React.MouseEvent) => {
    if (selectMode && onSelect) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(file);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectMode && onSelect) {
      event.stopPropagation();
      onSelect(file);
    }
  };

  if (isImportedFile) {
    return <ProcessingListItem name={name} />;
  }

  // Normal item display for non-processing files
  return (
    <ListItem
      data-testid="video-list-tile"
      disablePadding
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onMouseEnter={() => !hiddenForFreeUser && !isMobile && setShowMenu(true)}
      onMouseLeave={() => !hiddenForFreeUser && !isMobile && setShowMenu(false)}
      onClick={
        selectMode
          ? handleSelectionClick
          : () => {
              if (hiddenForFreeUser) {
                dispatch(pushModal({ type: 'upsell-modal' }));
              }
            }
      }
      sx={{
        borderBottom: theme.palette.mode === 'dark' ? 0 : 1,
        borderColor: theme.palette.secondary.light,
        position: 'relative',
        py: 1.5,
        pr: 2,
        backgroundColor: {
          xs:
            isSelected && selectMode
              ? theme.palette.action.selected
              : 'inherit',
          sm:
            (showMenu && !hiddenForFreeUser) || (isSelected && selectMode)
              ? theme.palette.background.default
              : 'inherit',
        },
        transition: 'background-color 0.3s',
        cursor: selectMode ? 'pointer' : 'default',
      }}
    >
      {selectMode ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(200px, 1fr) 65px 70px',
              sm: isInRecentsView
                ? 'minmax(200px, 1.5fr) 80px 100px 80px 24px'
                : 'minmax(200px, 1.5fr) 80px 80px 24px',
              md: isInRecentsView
                ? 'minmax(200px, 1.5fr) 80px 100px 120px 80px 24px'
                : 'minmax(200px, 1.5fr) 80px 120px 80px 24px',
              lg: isInRecentsView
                ? 'minmax(250px, 1.5fr) 100px 120px 120px 80px 24px'
                : 'minmax(250px, 1.5fr) 100px 150px 80px 24px',
            },
            alignItems: 'center',
            width: '100%',
            gap: { xs: 1, sm: 2 },
            pl: 2,
            pointerEvents: hiddenForFreeUser ? 'none' : 'auto',
            opacity: hiddenForFreeUser ? 0.5 : 1,
          }}
        >
          {/* Name Column */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
          >
            <Box sx={{ position: 'relative' }}>
              <Avatar
                alt={name}
                src={thumbUrlValid}
                sx={{
                  width: { xs: 24, sm: 32 },
                  height: { xs: 24, sm: 32 },
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 1,
                  flexShrink: 0,
                }}
                onError={() => setThumbUrlValid(defaultThumbnail)}
              />
              {selectMode && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(42, 43, 44, 0.9)'
                        : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={handleSelectionClick}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={handleCheckboxChange}
                    color="primary"
                    size="small"
                    sx={{
                      padding: '2px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.9rem',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {name}
            </Typography>
          </Box>

          {/* Duration Column */}
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {formatDuration(duration)}
          </Typography>

          {/* Folder Column */}
          {isInRecentsView && (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={handleMoveClick}
            >
              {parentId ? (
                <>
                  <Users
                    sx={{
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {parentFolder?.name || 'Unknown Folder'}
                  </Typography>
                </>
              ) : (
                <>
                  <Lock
                    sx={{
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    sx={{ opacity: 0.7, color: theme.palette.text.secondary }}
                  >
                    Private
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Creator Column */}
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            {ownerName}
          </Typography>

          {/* Date Column */}
          <Typography
            sx={{
              display: 'block',
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            {moment(createdAt).format('DD/MM/YY')}
          </Typography>
        </Box>
      ) : (
        <Link
          href={fileUrl}
          color="secondary.contrastText"
          underline="none"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(200px, 1fr) 65px 70px',
              sm: isInRecentsView
                ? 'minmax(200px, 1.5fr) 80px 100px 80px 24px'
                : 'minmax(200px, 1.5fr) 80px 80px 24px',
              md: isInRecentsView
                ? 'minmax(200px, 1.5fr) 80px 100px 120px 80px 24px'
                : 'minmax(200px, 1.5fr) 80px 120px 80px 24px',
              lg: isInRecentsView
                ? 'minmax(250px, 1.5fr) 100px 120px 120px 80px 24px'
                : 'minmax(250px, 1.5fr) 100px 150px 80px 24px',
            },
            alignItems: 'center',
            flexGrow: 1,
            gap: { xs: 1, sm: 2 },
            pl: 2,
            pointerEvents: hiddenForFreeUser ? 'none' : 'auto',
            opacity: hiddenForFreeUser ? 0.5 : 1,
          }}
        >
          {/* Name Column */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
          >
            <Avatar
              alt={name}
              src={thumbUrlValid}
              sx={{
                width: { xs: 24, sm: 32 },
                height: { xs: 24, sm: 32 },
                backgroundColor: theme.palette.secondary.main,
                borderRadius: 1,
                flexShrink: 0,
              }}
              onError={() => setThumbUrlValid(defaultThumbnail)}
            />
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {name}
            </Typography>
          </Box>

          {/* Duration Column */}
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {formatDuration(duration)}
          </Typography>

          {/* Folder Column */}
          {isInRecentsView && (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={handleMoveClick}
            >
              {parentId ? (
                <>
                  <Users
                    sx={{
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {parentFolder?.name || 'Unknown Folder'}
                  </Typography>
                </>
              ) : (
                <>
                  <Lock
                    sx={{
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    sx={{ opacity: 0.7, color: theme.palette.text.secondary }}
                  >
                    Private
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Creator Column */}
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            {ownerName}
          </Typography>

          {/* Date Column */}
          <Typography
            sx={{
              display: 'block',
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            {moment(createdAt).format('DD/MM/YY')}
          </Typography>
        </Link>
      )}
      {showMenu && !hiddenForFreeUser && (
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <FileActionMenu file={file} />
        </Box>
      )}
    </ListItem>
  );
};

export default FileListItem;
